import React, { useState, createElement } from 'react';
import { Calendar, ChevronLeft, ChevronRight, DollarSign, Download, ExternalLink } from 'lucide-react';
interface Rental {
  id: string;
  name: string;
  email: string;
  phone: string;
  eventName: string;
  customerInfo?: {
    address?: string;
    address2?: string;
    city?: string;
    state?: string;
    zip?: string;
    eventVenueCityState?: string;
    preferredTechnician?: string;
  };
  proposedEventDate: string;
  alternateDate: string;
  status: 'Pending' | 'Confirmed' | 'Deposit' | 'Paid in Full';
  total: number;
  subtotal: number;
  discount: number;
  tax: number;
  added: string;
  assignedTechnician?: string;
  assignedDistributor?: string;
  invoiceId?: string;
  selections: {
    gen3Body: number;
    gen3Head: number;
    gen3JudgesOnly: number;
    fullGen3Ring: number;
    requireTech: boolean;
    numberOfDays: number;
  };
}
interface CalendarViewProps {
  rentals: Rental[];
  onRentalClick: (rental: Rental) => void;
  getStatusColor: (status: string) => string;
  getStatusBadgeColor: (status: string) => string;
  getCoastFromLocation: (location: string) => string;
}
export function CalendarView({
  rentals,
  onRentalClick,
  getStatusColor,
  getStatusBadgeColor,
  getCoastFromLocation
}: CalendarViewProps) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarView, setCalendarView] = useState<'month' | 'list'>('month');
  const [groupByCoast, setGroupByCoast] = useState(false);
  const getDaysInMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };
  const getFirstDayOfMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };
  const getEventsForDate = (date: Date) => {
    const dateStr = date.toISOString().split('T')[0];
    return rentals.filter(rental => {
      // Check if event is on this date (proposed or alternate)
      if (rental.proposedEventDate === dateStr || rental.alternateDate === dateStr) {
        return true;
      }
      // For multi-day events, check if this date falls within the event duration
      if (rental.selections.numberOfDays > 1 && rental.proposedEventDate) {
        const eventDate = new Date(rental.proposedEventDate + 'T00:00:00');
        const checkDate = new Date(date);
        checkDate.setHours(0, 0, 0, 0);
        // Check each day of the event
        for (let i = 1; i < rental.selections.numberOfDays; i++) {
          const eventDay = new Date(eventDate);
          eventDay.setDate(eventDay.getDate() + i);
          const eventDayStr = eventDay.toISOString().split('T')[0];
          if (eventDayStr === dateStr) {
            return true;
          }
        }
      }
      return false;
    });
  };
  // Helper function to get actual ring count
  const getActualRingCount = (rental: Rental) => {
    return rental.selections.fullGen3Ring;
  };
  const downloadCSV = () => {
    const headers = ['Event Date', 'End Date', 'Days', 'Event Name', 'Customer Name', 'Email', 'Phone', 'City', 'State', 'Venue City/State', 'Status', 'Full Rings', 'Gen3 Body', 'Gen3 Head', 'Judges Only', 'Tech Required', 'Assigned Technician', 'Assigned Distributor', 'Subtotal', 'Discount', 'Tax', 'Total'];
    const escapeCSV = (value: string) => {
      if (value == null) return '';
      const str = String(value);
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };
    const rows = rentals.sort((a, b) => {
      if (!a.proposedEventDate) return 1;
      if (!b.proposedEventDate) return -1;
      return new Date(a.proposedEventDate).getTime() - new Date(b.proposedEventDate).getTime();
    }).map(rental => {
      const startDate = rental.proposedEventDate ? new Date(rental.proposedEventDate + 'T00:00:00').toLocaleDateString('en-US') : '';
      let endDate = '';
      if (rental.proposedEventDate && rental.selections.numberOfDays > 1) {
        const end = new Date(rental.proposedEventDate + 'T00:00:00');
        end.setDate(end.getDate() + rental.selections.numberOfDays - 1);
        endDate = end.toLocaleDateString('en-US');
      }
      return [startDate, endDate, rental.selections.numberOfDays, rental.eventName, rental.name, rental.email, rental.phone, rental.customerInfo?.city || '', rental.customerInfo?.state || '', rental.customerInfo?.eventVenueCityState || '', rental.status, rental.selections.fullGen3Ring, rental.selections.gen3Body, rental.selections.gen3Head, rental.selections.gen3JudgesOnly, rental.selections.requireTech ? 'Yes' : 'No', rental.assignedTechnician || '', rental.assignedDistributor || '', rental.subtotal.toFixed(2), rental.discount.toFixed(2), rental.tax.toFixed(2), rental.total.toFixed(2)].map(v => escapeCSV(String(v)));
    });
    const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csvContent], {
      type: 'text/csv;charset=utf-8;'
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `events_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };
  const previousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };
  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };
  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);
    const days = [];
    const monthName = currentDate.toLocaleString('default', {
      month: 'long',
      year: 'numeric'
    });
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="min-h-32 bg-slate-50 border border-slate-200" data-id="element-461"></div>);
    }
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
      const events = getEventsForDate(date);
      const isToday = new Date().toDateString() === date.toDateString();
      days.push(<div key={day} className={`min-h-32 border border-slate-200 p-2 ${isToday ? 'bg-blue-50 border-blue-400' : 'bg-white'}`} data-id="element-462">
          <div className={`text-sm font-semibold mb-2 ${isToday ? 'text-blue-800' : 'text-slate-700'}`} data-id="element-463">
            {day}
          </div>
          <div className="space-y-1" data-id="element-464">
            {events.map((event, idx) => <div key={idx} onClick={() => onRentalClick(event)} className="text-xs bg-white border rounded px-2 py-1 cursor-pointer hover:shadow-md transition-shadow" style={{
            borderLeftWidth: '3px',
            borderLeftColor: getStatusBadgeColor(event.status).replace('bg-', '#').replace('slate-400', '#94a3b8').replace('blue-500', '#3b82f6').replace('yellow-500', '#eab308').replace('green-500', '#22c55e')
          }} data-id="element-465">
                <div className="flex items-start justify-between" data-id="element-466">
                  <div className="flex-1 min-w-0" data-id="element-467">
                    <div className="font-semibold text-slate-800 truncate" data-id="element-468">
                      {event.eventName}
                    </div>
                    <div className="text-slate-600 truncate" data-id="element-469">{event.name}</div>
                    <div className="flex items-center justify-between mt-1" data-id="element-470">
                      <span className={`text-xs px-1 rounded ${getStatusColor(event.status)}`} data-id="element-471">
                        {event.status}
                      </span>
                      <span className="text-slate-500" data-id="element-472">
                        ${event.total.toFixed(0)}
                      </span>
                    </div>
                    <div className="text-xs text-blue-700 font-semibold mt-1" data-id="element-473">
                      {getActualRingCount(event)} Ring
                      {getActualRingCount(event) !== 1 ? 's' : ''}
                      {event.selections.numberOfDays > 1 && <span className="text-purple-600 ml-1" data-id="element-474">
                          ({event.selections.numberOfDays} Days)
                        </span>}
                    </div>
                  </div>
                  {(event.status === 'Deposit' || event.status === 'Paid in Full') && <div className="ml-1 flex-shrink-0" data-id="element-475">
                      <DollarSign size={16} className={event.status === 'Paid in Full' ? 'text-green-600' : 'text-yellow-600'} strokeWidth={3} data-id="element-476" />
                    </div>}
                </div>
              </div>)}
          </div>
        </div>);
    }
    return <div data-id="element-477">
        <div className="flex items-center justify-between mb-6" data-id="element-478">
          <button onClick={previousMonth} className="p-2 hover:bg-slate-100 rounded-lg transition-colors" data-id="element-479">
            <ChevronLeft size={24} data-id="element-480" />
          </button>
          <h3 className="text-2xl font-bold text-slate-800" data-id="element-481">{monthName}</h3>
          <button onClick={nextMonth} className="p-2 hover:bg-slate-100 rounded-lg transition-colors" data-id="element-482">
            <ChevronRight size={24} data-id="element-483" />
          </button>
        </div>

        {/* Legend */}
        <div className="flex gap-4 mb-4 text-sm" data-id="element-484">
          <div className="flex items-center gap-2" data-id="element-485">
            <div className="w-3 h-3 rounded bg-slate-400" data-id="element-486"></div>
            <span data-id="element-487">Pending</span>
          </div>
          <div className="flex items-center gap-2" data-id="element-488">
            <div className="w-3 h-3 rounded bg-blue-500" data-id="element-489"></div>
            <span data-id="element-490">Confirmed</span>
          </div>
          <div className="flex items-center gap-2" data-id="element-491">
            <div className="w-3 h-3 rounded bg-yellow-500" data-id="element-492"></div>
            <span data-id="element-493">Deposit</span>
          </div>
          <div className="flex items-center gap-2" data-id="element-494">
            <div className="w-3 h-3 rounded bg-green-500" data-id="element-495"></div>
            <span data-id="element-496">Paid in Full</span>
          </div>
        </div>

        <div className="grid grid-cols-7 gap-0 border border-slate-300 rounded-lg overflow-hidden" data-id="element-497">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => <div key={day} className="bg-slate-100 border-b border-slate-300 p-3 text-center font-semibold text-slate-700" data-id="element-498">
              {day}
            </div>)}
          {days}
        </div>
      </div>;
  };
  const renderListView = () => {
    const rentalsByMonthYear = rentals.reduce((acc, rental) => {
      if (!rental.proposedEventDate) {
        if (!acc['No Date']) acc['No Date'] = [];
        acc['No Date'].push(rental);
        return acc;
      }
      const date = new Date(rental.proposedEventDate);
      const monthYear = date.toLocaleString('default', {
        month: 'long',
        year: 'numeric'
      });
      if (!acc[monthYear]) acc[monthYear] = [];
      acc[monthYear].push(rental);
      return acc;
    }, {} as Record<string, Rental[]>);
    const sortedMonthYears = Object.keys(rentalsByMonthYear).sort((a, b) => {
      if (a === 'No Date') return 1;
      if (b === 'No Date') return -1;
      const dateA = new Date(rentalsByMonthYear[a][0].proposedEventDate);
      const dateB = new Date(rentalsByMonthYear[b][0].proposedEventDate);
      return dateA.getTime() - dateB.getTime();
    });
    return <div className="space-y-6" data-id="element-499">
        {sortedMonthYears.map(monthYear => {
        const monthRentals = rentalsByMonthYear[monthYear];
        return <div key={monthYear} className="border border-slate-200 rounded-lg overflow-hidden" data-id="element-500">
              <div className="bg-slate-100 px-4 py-3 border-b border-slate-200" data-id="element-501">
                <div className="flex items-center justify-between" data-id="element-502">
                  <h3 className="text-lg font-semibold text-slate-800" data-id="element-503">
                    {monthYear}
                  </h3>
                  <span className="text-sm text-slate-600" data-id="element-504">
                    {monthRentals.length} event
                    {monthRentals.length !== 1 ? 's' : ''}
                  </span>
                </div>
              </div>

              <div className="divide-y divide-slate-200" data-id="element-505">
                {monthRentals.sort((a, b) => {
              if (!a.proposedEventDate) return 1;
              if (!b.proposedEventDate) return -1;
              return new Date(a.proposedEventDate).getTime() - new Date(b.proposedEventDate).getTime();
            }).map(rental => <div key={rental.id} onClick={() => onRentalClick(rental)} className="p-4 hover:bg-slate-50 cursor-pointer transition-colors" data-id="element-506">
                      <div className="flex items-center justify-between" data-id="element-507">
                        <div className="flex-1 min-w-0" data-id="element-508">
                          <div className="flex items-center gap-3 mb-1" data-id="element-509">
                            <span className="font-semibold text-slate-800" data-id="element-510">
                              {rental.proposedEventDate ? (() => {
                        const startDate = new Date(rental.proposedEventDate + 'T00:00:00');
                        const startDateStr = startDate.toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric'
                        });
                        if (rental.selections.numberOfDays > 1) {
                          const endDate = new Date(startDate);
                          endDate.setDate(endDate.getDate() + rental.selections.numberOfDays - 1);
                          const endDateStr = endDate.toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric'
                          });
                          return `${startDateStr} - ${endDateStr}`;
                        }
                        return startDateStr;
                      })() : 'No date'}
                            </span>
                            <span className={`px-2 py-0.5 rounded text-xs font-medium border ${getStatusColor(rental.status)}`} data-id="element-511">
                              {rental.status}
                            </span>
                            <span className="text-xs font-semibold text-blue-700" data-id="element-512">
                              {getActualRingCount(rental)} Ring
                              {getActualRingCount(rental) !== 1 ? 's' : ''}
                              {rental.selections.numberOfDays > 1 && <span className="text-purple-600 ml-1" data-id="element-513">
                                  ({rental.selections.numberOfDays} Days)
                                </span>}
                            </span>
                          </div>
                          <div className="text-sm text-slate-600 truncate" data-id="element-514">
                            {rental.eventName} • {rental.name}
                          </div>
                          <div className="text-xs text-slate-500 mt-1" data-id="element-515">
                            {rental.customerInfo?.eventVenueCityState || [rental.customerInfo?.city, rental.customerInfo?.state].filter(Boolean).join(', ') || 'No location'}
                          </div>
                        </div>
                        <div className="text-right ml-4" data-id="element-516">
                          <div className="font-semibold text-slate-800" data-id="element-517">
                            ${rental.total.toFixed(2)}
                          </div>
                          {rental.selections.requireTech && <div className="text-xs text-red-600 mt-1" data-id="element-518">
                              Tech Required
                            </div>}
                        </div>
                      </div>
                    </div>)}
              </div>
            </div>;
      })}

        {sortedMonthYears.length === 0 && <div className="text-center py-12" data-id="element-519">
            <Calendar size={48} className="text-slate-300 mx-auto mb-4" data-id="element-520" />
            <p className="text-slate-600" data-id="element-521">No events scheduled</p>
          </div>}
      </div>;
  };
  return <div className="p-6" data-id="element-522">
      {/* View Toggle */}
      <div className="flex items-center justify-between mb-6" data-id="element-523">
        <div className="flex gap-2" data-id="element-524">
          <button onClick={() => setCalendarView('month')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${calendarView === 'month' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`} data-id="element-525">
            Month View
          </button>
          <button onClick={() => setCalendarView('list')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${calendarView === 'list' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`} data-id="element-526">
            List View
          </button>
        </div>

        <div className="flex items-center gap-4" data-id="element-527">
          {calendarView === 'list' && <>
              <button onClick={downloadCSV} className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium" data-id="element-528">
                <Download size={16} data-id="element-529" />
                Download CSV
              </button>
              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer" data-id="element-530">
                <input type="checkbox" checked={groupByCoast} onChange={e => setGroupByCoast(e.target.checked)} className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500" data-id="element-531" />
                <span data-id="element-532">Group by Coast</span>
              </label>
            </>}
          <a href="/schedule" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-4 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition-colors text-sm font-medium" data-id="element-533">
            <ExternalLink size={16} data-id="element-534" />
            External View
          </a>
        </div>
      </div>

      {calendarView === 'month' ? renderCalendar() : renderListView()}
    </div>;
}